import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Trophy, Download } from "@phosphor-icons/react";

const COLS = [
  ["sales_calls", "Calls"], ["sales_emails", "Emails"], ["proposals_sent", "Proposals"],
  ["property_tours", "Tours"], ["contracts_sent", "Contracts"], ["blocks_created", "Blocks"],
  ["beos_created", "BEOs"], ["new_prospect_business", "New Prospect"],
  ["new_tentative_business", "New Tentative"], ["new_definite_business", "New Definite"],
];

function isoDaysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

export default function SalesReport() {
  const [start, setStart] = useState(isoDaysAgo(30));
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const { data } = useQuery({ queryKey: ["sales-report", start, end], queryFn: () => api.salesReport({ start, end }) });
  const reps = data?.reps || [];

  const exportCsv = () => {
    const header = ["Salesperson", "Title", ...COLS.map((c) => c[1])];
    const lines = [header.join(",")];
    reps.forEach((r) => lines.push([`"${r.name}"`, `"${r.title || ""}"`, ...COLS.map((c) => r[c[0]] || 0)].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `sales-activity_${start}_${end}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4" data-testid="sales-report">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">From</span>
            <input data-testid="report-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="border border-[#E5E7EB] px-3 py-2 text-sm" /></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">To</span>
            <input data-testid="report-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="border border-[#E5E7EB] px-3 py-2 text-sm" /></label>
        </div>
        <button data-testid="report-export" onClick={exportCsv} className="inline-flex items-center gap-1.5 border border-[#111827] px-3 py-2 text-sm font-semibold hover:bg-[#F3F4F6]"><Download size={15} weight="bold" /> Export CSV</button>
      </div>

      <div className="border border-[#E5E7EB] overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#F3F4F6]">
              <th className="text-left px-3 py-2 text-xs font-semibold text-[#111827] sticky left-0 bg-[#F3F4F6]">Salesperson</th>
              {COLS.map((c) => <th key={c[0]} className="px-2 py-2 text-xs font-semibold text-[#4B5563] whitespace-nowrap">{c[1]}</th>)}
            </tr>
          </thead>
          <tbody>
            {reps.map((r, i) => (
              <tr key={r.id} data-testid={`report-row-${r.id}`} className="border-t border-[#E5E7EB]">
                <td className="px-3 py-2 sticky left-0 bg-white">
                  <span className="font-semibold text-[#111827] inline-flex items-center gap-1">{i === 0 && (r.sales_calls + r.sales_emails + r.new_definite_business) > 0 && <Trophy size={14} weight="fill" color="#D97706" />}{r.name}</span>
                  <span className="block text-xs text-[#9CA3AF]">{r.title}</span>
                </td>
                {COLS.map((c) => <td key={c[0]} className={`px-2 py-2 text-center ${c[0] === "new_definite_business" ? "font-bold text-[#047857]" : "text-[#4B5563]"}`}>{r[c[0]] || 0}</td>)}
              </tr>
            ))}
            {reps.length === 0 && <tr><td colSpan={COLS.length + 1} className="px-3 py-8 text-center text-sm text-[#4B5563]" data-testid="report-empty">No activity in this range.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
